import type { PreviousAddressRow, ApplyValidateAddressOptions } from '../types/utils'
import type { AddressData } from '@/types/application'
/** Angular previous-address parity (`getAddressMonth`, `validateAddress`, etc.). */

import { calendarMonthsBetween } from "@/features/applicant/utils/addressHelpers"


const trim = (value: string | undefined | null): string => (value ?? "").toString().trim()

/** Empty previous-address row (Angular `getControls('address')`). */
export function createEmptyPreviousAddressRow(): PreviousAddressRow {
  return {
    ra_id: "",
    street_address: "",
    outside_us: false,
    house_no: "",
    street: "",
    apartment: "",
    city: "",
    state: "",
    zip_code: "",
    country: "US",
    address_type: "RENT",
    address_data: {},
  }
}

const addressDataFields = (
  addr: PreviousAddressRow | undefined | null,
): AddressData => {
  if (!addr) return {}
  const ad = addr.address_data
  if (ad && typeof ad === "object") return ad
  return {}
}

/** Drop holes / null rows from a previous-address list. */
export function normalizePreviousAddressRows(
  rows: Array<PreviousAddressRow | undefined | null> | undefined | null,
): PreviousAddressRow[] {
  if (!Array.isArray(rows)) return []
  return rows.filter((row): row is PreviousAddressRow => Boolean(row))
}

/** True when a row carries saved/imported address content (not a validateAddress placeholder). */
export function hasSubstantivePreviousAddressRow(
  row: PreviousAddressRow | undefined | null,
): boolean {
  if (!row) return false
  if (row.ra_id) return true
  if (
    trim(row.house_no) ||
    trim(row.street) ||
    trim(row.street_address) ||
    trim(row.city)
  ) {
    return true
  }
  if (trim(moveDateValue(row, "moveIn_date"))) return true
  const raw = monthFieldValue(row)
  return raw !== undefined && raw !== null && raw !== ""
}

/** Drop auto-added empty rows so API/import seeds can replace placeholders. */
export function filterPlaceholderPreviousRows(
  rows: PreviousAddressRow[],
): PreviousAddressRow[] {
  const normalized = normalizePreviousAddressRows(rows)
  const substantive = normalized.filter(hasSubstantivePreviousAddressRow)
  return substantive.length > 0 ? substantive : []
}

/** Prefer in-form rows; fall back to imported/saved API rows when only placeholders exist. */
export function resolvePreviousFormsForPipeline(
  prevForms: PreviousAddressRow[],
  seedFromApi?: PreviousAddressRow[] | null,
): PreviousAddressRow[] {
  const substantive = filterPlaceholderPreviousRows(prevForms)
  if (substantive.length > 0) return substantive
  return normalizePreviousAddressRows(seedFromApi)
}

/** `address_data` first, then root (React form mirrors patched `address_data`). */
const monthFieldValue = (addr: PreviousAddressRow | undefined | null): unknown => {
  if (!addr) return undefined
  const type = trim(addr.address_type).toUpperCase()
  const key = type === "OWN" ? "month_reside" : "paid_rent"
  const ad = addressDataFields(addr)
  if (ad[key] !== undefined && ad[key] !== null) return ad[key]
  return addr[key]
}

const isStillLiving = (addr: PreviousAddressRow): boolean => {
  const ad = addressDataFields(addr)
  const flag = ad.currently_living ?? addr.currently_living
  return flag === true || flag === "1"
}

const moveDateValue = (
  addr: PreviousAddressRow | undefined | null,
  key: "moveIn_date" | "moveOut_date",
): string => {
  if (!addr) return ""
  const ad = addressDataFields(addr)
  return trim(String(ad[key] ?? addr[key] ?? ""))
}

/** Angular `moveInValidate()` — months from dates; null when incomplete. */
export function computeMoveInValidateMonths(
  addr: PreviousAddressRow | undefined | null,
): number | null {
  if (!addr) return null
  const moveIn = moveDateValue(addr, "moveIn_date")
  if (!moveIn) return null

  let end: string | undefined
  if (isStillLiving(addr)) {
    end = undefined
  } else {
    const moveOut = moveDateValue(addr, "moveOut_date")
    if (!moveOut) return null
    end = moveOut
  }

  const months = calendarMonthsBetween(moveIn, end)
  return months > 0 ? months : 0
}

/** Apply `moveInValidate` month write onto address (root + `address_data`). */
export function applyMoveInValidateMonths(addr: PreviousAddressRow): PreviousAddressRow {
  const months = computeMoveInValidateMonths(addr)
  if (months === null) return addr

  const type = trim(addr.address_type).toUpperCase()
  const key = type === "OWN" ? "month_reside" : "paid_rent"
  const monthsStr = String(months)
  const ad = { ...addressDataFields(addr) }

  return {
    ...addr,
    [key]: monthsStr,
    address_data: {
      ...ad,
      moveIn_date: (moveDateValue(addr, "moveIn_date") || ad.moveIn_date) as
        | string
        | undefined,
      moveOut_date: (moveDateValue(addr, "moveOut_date") || ad.moveOut_date) as
        | string
        | null
        | undefined,
      currently_living: (ad.currently_living ?? addr.currently_living) as
        | string
        | boolean
        | undefined,
      [key]: monthsStr,
    },
  }
}

/** Angular `getAddressMonth` — truthy `month_reside`/`paid_rent` only. */
export function getAddressMonth(addr: PreviousAddressRow | undefined | null): number {
  if (!addr) return 0
  const raw = monthFieldValue(addr)
  if (raw === undefined || raw === null || raw === "" || raw === false) {
    return 0
  }
  const n = parseInt(String(raw), 10)
  return Number.isFinite(n) ? n : 0
}

/** Angular `checkPreviousAddress()` — months field < 24 (loose compare). */
export function checkPreviousAddress(addr: PreviousAddressRow | undefined | null): boolean {
  if (!addr) return false
  const raw = monthFieldValue(addr)
  // Angular `addressControl.get('paid_rent').value < 24` — null/undefined/empty count as < 24.
  if (raw === undefined || raw === null || raw === "") {
    return true
  }
  return Number(raw) < 24
}

/** Angular `previousAddressMonths()` for one previous row. */
export function computePreviousRowMonths(
  row: PreviousAddressRow,
  index: number,
  currentMoveInYmd: string,
  previousRows: PreviousAddressRow[],
): number | null {
  const moveIn = moveDateValue(row, "moveIn_date")
  if (!moveIn) return null

  let endMoveIn = currentMoveInYmd
  if (index > 0) {
    endMoveIn = moveDateValue(previousRows[index - 1], "moveIn_date") || endMoveIn
  }
  if (!endMoveIn) return null

  const months = calendarMonthsBetween(moveIn, endMoveIn)
  return months > 0 ? months : 0
}

/** Apply `previousAddressMonths` across all previous rows. */
export function applyPreviousAddressMonths(
  previousRows: PreviousAddressRow[],
  currentMoveInYmd: string,
): PreviousAddressRow[] {
  const rows = normalizePreviousAddressRows(previousRows)
  if (!rows.length || !currentMoveInYmd) return rows

  let lastMoveIn = currentMoveInYmd
  return rows.map((row, i) => {
    const moveIn = moveDateValue(row, "moveIn_date")
    if (!moveIn) return row

    const endRef = i === 0 ? currentMoveInYmd : lastMoveIn
    const months = calendarMonthsBetween(moveIn, endRef)
    const type = trim(row.address_type).toUpperCase()
    const key = type === "OWN" ? "month_reside" : "paid_rent"
    // A computed duration under one month is a real "0", not "unknown" — show "0" rather than
    // an empty field (the months input is read-only and required).
    const monthsStr = String(months > 0 ? months : 0)
    lastMoveIn = moveIn

    const ad = { ...addressDataFields(row) }
    return {
      ...row,
      [key]: monthsStr,
      address_data: { ...ad, [key]: monthsStr, moveIn_date: moveIn },
    }
  })
}


/** Angular `validateAddress()` — auto add/remove previous rows. */
export function applyValidateAddress(
  currentAddr: PreviousAddressRow | undefined | null,
  previousForms: PreviousAddressRow[],
  options?: ApplyValidateAddressOptions,
): PreviousAddressRow[] {
  const currentMonths = getAddressMonth(currentAddr)
  if (currentMonths !== 0 && !currentMonths) {
    return previousForms
  }

  if (currentMonths >= 24) {
    return previousForms.length === 0 ? previousForms : []
  }

  let working = filterPlaceholderPreviousRows(previousForms)

  if (working.length === 0) {
    const seed = options?.seedFromApi
    if (Array.isArray(seed) && seed.length > 0) {
      working = [...seed]
    } else {
      return [createEmptyPreviousAddressRow()]
    }
  }

  let previousMonth = 0
  for (let i = 0; i < working.length; i++) {
    const monthValue = getAddressMonth(working[i])
    if (!monthValue) {
      return working
    }
    previousMonth += monthValue
  }

  if (currentMonths < 24 && currentMonths + previousMonth < 24) {
    return [...working, createEmptyPreviousAddressRow()]
  }

  return working
}

/** Prefer in-progress edit address over saved for previous-section rules. */
export function resolveCurrentAddressForPreviousRules(
  edited: PreviousAddressRow | undefined | null,
  saved: PreviousAddressRow | undefined | null,
): PreviousAddressRow | null {
  if (edited) return edited
  if (saved) return saved
  return null
}

/** Show previous edit when current valid (optional) + needs previous + rows exist (Angular). */
export function shouldShowPreviousAddressEditSection(
  currentAddr: PreviousAddressRow | undefined | null,
  previousForms: PreviousAddressRow[],
  isCurrentAddressValid: boolean,
  options?: { requireFullCurrentValid?: boolean },
): boolean {
  const requireFull = options?.requireFullCurrentValid !== false
  // Angular template: `current_address.valid && checkPreviousAddress()` — includes 0 months.
  const needsPrevious = checkPreviousAddress(currentAddr)
  const gate = requireFull ? isCurrentAddressValid && needsPrevious : needsPrevious
  return gate && previousForms.length > 0
}

/** Full current-address pipeline: moveInValidate → previousAddressMonths → validateAddress. */
export function runCurrentAddressAddressPipeline(
  currentAddr: PreviousAddressRow | undefined | null,
  previousForms: PreviousAddressRow[],
  options?: ApplyValidateAddressOptions,
): { current: PreviousAddressRow | null; previous: PreviousAddressRow[] } {
  if (!currentAddr) {
    return { current: null, previous: previousForms }
  }

  const current = applyMoveInValidateMonths(currentAddr)
  const moveIn = moveDateValue(current, "moveIn_date")
  let previous = previousForms

  if (moveIn && previous.length > 0) {
    previous = applyPreviousAddressMonths(previous, moveIn)
  }

  previous = applyValidateAddress(current, previous, options)
  return { current, previous }
}
